import React, { useState, useEffect } from "react";
import { Button, Container, FormControl } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import authApi from "../../data/api/authApi";
import { useLanguage } from "../helpers/LanguageContext";
import "../../css/login.css";

const Login = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [isTokenChecked, setIsTokenChecked] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();

    if (isOffline) {
      setErrorMessage(t("loginOfflineError"));
      return;
    }

    try {
      await authApi.loginApi(username, password, rememberMe);
      setErrorMessage("");

      if (password === "Welcome1") {
        navigate("/reset-password", { replace: true });
        return;
      }

      setIsLoggedIn(true);
      navigate("/dashboard", { replace: true });
    } catch (error) {
      setErrorMessage(t("loginError"));
      console.error("Login failed:", error.message);
    }
  };

  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  useEffect(() => {
    if (!errorMessage) return;

    const timer = setTimeout(() => {
      setErrorMessage("");
    }, 5000);

    return () => clearTimeout(timer);
  }, [errorMessage]);

  useEffect(() => {
    const checkToken = async () => {
      try {
        const token = localStorage.getItem("userToken");
        if (token) {
          await authApi.checkTokenApi(token);
          setIsLoggedIn(true);
          navigate("/dashboard", { replace: true });
        }
      } catch (error) {
        if (error.response && error.response.status === 401) {
          console.log("Unauthorized: Invalid token.");
        } else {
          console.error("Token check failed:", error.message);
        }
      } finally {
        setIsTokenChecked(true);
      }
    };

    checkToken();
  }, [navigate]);

  return (
    <Container fluid className="login-page">
      <div className="login-card shadow-sm">
        {!isTokenChecked ? (
          <div className="login-loading">{t("loading")}</div>
        ) : (
          <form onSubmit={handleLogin} className="login-form">
            <h3 className="login-title">{t("loginTitle")}</h3>

            {!isLoggedIn && errorMessage && (
              <div className="alert alert-danger">{errorMessage}</div>
            )}

            <div className="mb-3">
              <label className="form-label">{t("username")}</label>
              <FormControl
                type="text"
                placeholder={t("usernamePlaceholder")}
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                className="login-input"
              />
            </div>

            <div className="mb-3">
              <label className="form-label">{t("password")}</label>
              <div className="login-password-wrapper">
                <FormControl
                  type={showPassword ? "text" : "password"}
                  placeholder={t("passwordPlaceholder")}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="login-input login-password-input"
                />
                <Button
                  variant="link"
                  onClick={() => setShowPassword((v) => !v)}
                  className="login-password-toggle"
                  tabIndex={-1}
                >
                  <i
                    className={`bi ${showPassword ? "bi-eye-slash" : "bi-eye"}`}
                  />
                </Button>
              </div>
            </div>

            <div className="d-flex justify-content-between align-items-center mb-3">
              <div className="form-check">
                <input
                  type="checkbox"
                  id="rememberMe"
                  className="form-check-input"
                  checked={rememberMe}
                  onChange={() => setRememberMe((r) => !r)}
                />
                <label className="form-check-label" htmlFor="rememberMe">
                  {t("rememberMe")}
                </label>
              </div>
            </div>

            {isOffline && (
              <div className="alert alert-warning">
                {t("loginOfflineWarning")}
              </div>
            )}

            <div className="d-grid gap-2 login-actions">
              <Button type="submit" className="login-submit-btn">
                {t("loginTitle")}
              </Button>

              <Button
                variant="link"
                className="login-link-btn"
                onClick={() => navigate("/register")}
              >
                {t("register")}
              </Button>

              <Button
                variant="link"
                className="login-link-btn"
                onClick={() => navigate("/forgot-password")}
              >
                {t("forgotPassword")}
              </Button>
            </div>
          </form>
        )}
      </div>
    </Container>
  );
};

export default Login;
